import { useQuery } from '@apollo/client';
import { graphql } from '@gql/gql';
import type {
  ProSearchGetProsQuery,
  ProSearchGetProsQueryVariables,
  ProfessionalTypeEnum,
  ProfessionalWhereInput,
} from '@gql/graphql';
import { CountryCode, QueryMode } from '@gql/graphql';
import { useMemo } from 'react';
import removeAccents from 'remove-accents';
import { useDebounce } from '../../useDebounce';
import { useRouter } from '../../useRouter';

export const PRO_SEARCH_GET_PROS = graphql(`
  query ProSearchGetPros($where: ProfessionalWhereInput) {
    searchProfessionals(where: $where, take: 5) {
      professionals {
        id
        type
        centerType

        address {
          id
          city
        }

        imgs(take: 1) {
          id
          medium
        }

        translations {
          id
          language
          url
          title
        }
      }
    }
  }
`);

interface UseGetProsParams {
  type: ProfessionalTypeEnum;
  skip?: boolean;
  searchString?: string;
}

export const useGetPros = (params: UseGetProsParams) => {
  const { type, skip } = params;
  const searchString = useDebounce(params.searchString, 250);
  const { countryCode } = useRouter();

  // show only claimed pros for belgium
  const showOnlyClaimed = countryCode === CountryCode.BE;

  const variables = useMemo((): ProSearchGetProsQueryVariables => {
    return {
      where: {
        AND: [
          { type: { equals: type } },
          { published: { equals: true } },
          ...(showOnlyClaimed ? [{ unclaimedProfile: { equals: false } }] : []),
          ...(searchString
            ? [
                {
                  searchString: {
                    contains: removeAccents(searchString),
                    mode: QueryMode.insensitive,
                  },
                },
              ]
            : []),
          ...(countryCode && countryCode !== CountryCode.EU
            ? [{ address: { is: { country: { equals: countryCode } } } }]
            : []),
        ],
      } as ProfessionalWhereInput,
    };
  }, [type, showOnlyClaimed, searchString, countryCode]);

  const { data, error, loading } = useQuery<ProSearchGetProsQuery, ProSearchGetProsQueryVariables>(
    PRO_SEARCH_GET_PROS,
    { variables, skip },
  );

  return {
    pros: data?.searchProfessionals.professionals ?? [],
    prosError: error,
    prosLoading: loading,
  };
};
